import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import { FiLayout, FiCheckCircle, FiDatabase, FiSettings, FiPaperclip, FiShield, FiZap, FiBox, FiMail, FiPhone, FiMapPin, FiUser, FiExternalLink, FiCalendar, FiX, FiArrowRight, FiFileText, FiMousePointer } from 'react-icons/fi';
import { categories, formSchemas } from './config/formsConfig';
import DynamicForm from './components/DynamicForm';
import '@questlabs/react-sdk/dist/style.css';

function App() {
  const [activeCategory, setActiveCategory] = useState(categories[0].id);
  const [activeFormIndex, setActiveFormIndex] = useState(0);
  const [showIntroModal, setShowIntroModal] = useState(false);

  useEffect(() => {
    document.title = "BPA Solutions | Smart Forms Showcase – Live Demo";
    const svgFavicon = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
        <rect width="64" height="64" rx="14" fill="#0f172a"/>
        <path d="M22 42 C 22 42,22 22,32 22 C 42 22,42 32,32 32 C 22 32,22 42,32 42 C 42 42,42 22,42 22" stroke="#3b82f6" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="22" cy="22" r="3" fill="#eab308"/>
      </svg>
    `;
    const encodedFavicon = `data:image/svg+xml;base64,${btoa(svgFavicon)}`;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = encodedFavicon;

    const hasSeenDemo = sessionStorage.getItem('bpa_demo_seen');
    if (!hasSeenDemo) {
      setShowIntroModal(true);
    }
  }, []);

  useEffect(() => {
    setActiveFormIndex(0);
  }, [activeCategory]);

  const closeIntro = () => {
    sessionStorage.setItem('bpa_demo_seen', 'true');
    setShowIntroModal(false);
  };

  const scrollToDemo = () => {
    closeIntro();
    const element = document.getElementById('demo-form-start');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const activeForms = formSchemas[activeCategory] || [];
  const activeFormSchema = activeForms[activeFormIndex];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-blue-200">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiBox} className="text-blue-400 text-xl" />
              </div>
              <span className="font-bold text-xl tracking-tight text-slate-900">BPA Solutions</span>
            </div>
            <div className="hidden sm:flex items-center gap-4 text-sm font-medium text-slate-600">
              <span>Smart Forms Showcase</span>
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-[10px] font-black tracking-widest uppercase">Live Demo</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="bg-blue-600 text-white py-2.5 px-4 sticky top-16 z-40 shadow-md">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-tight">
            🚀 Submit a form, verify your details, and see how BPA Solutions automates verified enquiries, waivers, and acknowledgements.
          </p>
          <button onClick={scrollToDemo} className="bg-white text-blue-600 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-wider hover:bg-blue-50 transition-all shadow-sm active:scale-95" > Try Demo </button>
        </div>
      </div>

      <div className="bg-slate-900 text-white pt-20 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-400 via-slate-900 to-slate-900"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-5xl lg:text-7xl font-black mb-6 tracking-tighter"> Smart Forms Showcase </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed font-medium"> 
            A live demonstration of verified business forms for enquiries, bookings, intake, <strong>digital waivers</strong>, and customer acknowledgements.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex flex-wrap justify-center gap-4 md:gap-6 text-[10px] font-black uppercase tracking-widest text-slate-300 mb-12"> 
            <div className="flex items-center gap-2 bg-white/5 rounded-full px-5 py-2.5 border border-white/10"><SafeIcon icon={FiShield} className="text-blue-400" /> Verify real customers</div> 
            <div className="flex items-center gap-2 bg-white/5 rounded-full px-5 py-2.5 border border-white/10"><SafeIcon icon={FiFileText} className="text-green-400" /> Capture signed consent</div> 
            <div className="flex items-center gap-2 bg-white/5 rounded-full px-5 py-2.5 border border-white/10"><SafeIcon icon={FiZap} className="text-yellow-400" /> Automate follow-up</div> 
          </motion.div>
          <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} onClick={scrollToDemo} className="bg-blue-600 hover:bg-blue-500 text-white px-10 py-4 rounded-full font-black text-sm uppercase tracking-widest transition-all shadow-xl shadow-blue-600/30 hover:scale-105 active:scale-95"> Explore Smart Forms </motion.button>
        </div>
      </div>

      <div className="bg-white py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-2 uppercase tracking-tight">How the verification works</h2>
            <p className="text-slate-500 font-medium">A simple 3-step process shared across all implementations.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[ 
              { step: '1', title: 'Choose a smart form', desc: 'Select any industry-specific template from the library below.', icon: FiLayout },
              { step: '2', title: 'Verify email or phone', desc: 'Users must verify contact details before the form unlocks.', icon: FiShield },
              { step: '3', title: 'Submit a trusted record', desc: 'Complete the form to generate a verified business record.', icon: FiCheckCircle } 
            ].map((item, idx) => ( 
              <div key={idx} className="flex flex-col items-center text-center p-8 rounded-3xl bg-slate-50 border border-slate-100 relative shadow-sm hover:shadow-md transition-shadow"> 
                <div className="absolute -top-4 w-10 h-10 bg-blue-600 text-white rounded-2xl flex items-center justify-center font-black text-sm shadow-lg"> {item.step} </div> 
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 text-blue-600 text-2xl border border-slate-100"> <SafeIcon icon={item.icon} /> </div> 
                <h3 className="font-extrabold text-slate-900 mb-3 tracking-tight">{item.title}</h3> 
                <p className="text-slate-500 text-sm leading-relaxed font-medium">{item.desc}</p> 
              </div> 
            ))} 
          </div>
        </div>
      </div>

      <div id="demo-area" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div id="demo-form-start" className="mb-12 flex flex-col items-center scroll-mt-32"> 
          <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-8">Select Industry / Category</h2> 
          <div className="flex flex-wrap justify-center gap-3 bg-white p-3 rounded-2xl shadow-lg border border-slate-200"> 
            {categories.map((cat) => ( 
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeCategory === cat.id ? 'bg-slate-900 text-white shadow-xl scale-105' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`} > {cat.label} </button> 
            ))} 
          </div> 
        </div>

        <div className="mb-10 flex flex-wrap justify-center gap-3"> 
          {activeForms.map((form, idx) => ( 
            <button key={form.id} onClick={() => setActiveFormIndex(idx)} className={`px-5 py-2.5 rounded-xl text-xs font-bold border transition-all ${activeFormIndex === idx ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`} > {form.title} </button> 
          ))} 
        </div>

        <div className="max-w-5xl mx-auto"> 
          <AnimatePresence mode="wait"> 
            <motion.div key={`${activeCategory}-${activeFormIndex}`} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.3 }} > 
              <DynamicForm formSchema={activeFormSchema} categoryId={activeCategory} /> 
            </motion.div> 
          </AnimatePresence> 
        </div>
      </div>

      <div className="bg-white border-t border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-slate-50 p-10 rounded-3xl border border-slate-100 shadow-sm">
              <div className="flex items-center gap-4 mb-6"> 
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={FiDatabase} className="text-blue-600 text-2xl" /> 
                </div>
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Verified Records</h3> 
              </div>
              <p className="text-slate-600 mb-8 leading-relaxed font-medium"> 
                BPA Solutions provides a robust architecture that separates the <strong>identity verification engine</strong> from the specific form interfaces. This ensures every record—from enquiries to <strong>digital waivers</strong>—is trusted.
              </p>
              <ul className="space-y-3 text-sm font-bold text-slate-700 uppercase tracking-tight"> 
                <li className="flex items-center gap-3"><SafeIcon icon={FiCheckCircle} className="text-green-500" /> <span>Prevents bot submissions</span></li> 
                <li className="flex items-center gap-3"><SafeIcon icon={FiCheckCircle} className="text-green-500" /> <span>Verified Waiver & Consent records</span></li> 
                <li className="flex items-center gap-3"><SafeIcon icon={FiCheckCircle} className="text-green-500" /> <span>Unified data for CRM ingestion</span></li> 
              </ul>
            </div>
            <div className="bg-slate-50 p-10 rounded-3xl border border-slate-100 shadow-sm">
              <div className="flex items-center gap-4 mb-6"> 
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <SafeIcon icon={FiSettings} className="text-blue-600 text-2xl" /> 
                </div>
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Custom Workflows</h3> 
              </div>
              <p className="text-slate-600 mb-8 leading-relaxed font-medium"> 
                Beyond lead capture, we automate the entire lifecycle of a form submission including conditional routing, branded acknowledgments, and real-time logging.
              </p>
              <ul className="space-y-3 text-sm font-bold text-slate-700 uppercase tracking-tight"> 
                <li className="flex items-center gap-3"><SafeIcon icon={FiCheckCircle} className="text-green-500" /> <span>Advanced conditional logic</span></li> 
                <li className="flex items-center gap-3"><SafeIcon icon={FiCheckCircle} className="text-green-500" /> <span>Google Sheets & CRM integration</span></li> 
                <li className="flex items-center gap-3"><SafeIcon icon={FiCheckCircle} className="text-green-500" /> <span>Branded automated follow-ups</span></li> 
              </ul>
            </div>
          </div>
          <div className="mt-12 bg-blue-50 border border-blue-100 rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 text-blue-900 shadow-sm"> 
            <div className="bg-blue-100 p-4 rounded-2xl text-blue-600 shrink-0"> <SafeIcon icon={FiPaperclip} className="text-2xl" /> </div> 
            <div> 
              <h4 className="font-black text-lg mb-1 uppercase tracking-tight">Need attachments or document uploads?</h4> 
              <p className="text-sm font-medium opacity-80 leading-relaxed"> BPA Solutions customises smart forms to support secure file uploads, image captures, and document attachments where required, integrated directly into your existing admin workflows. </p> 
            </div> 
          </div>
        </div>
      </div>

      <footer className="bg-slate-900 text-slate-300 py-16 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
                  <SafeIcon icon={FiBox} className="text-white text-2xl" />
                </div>
                <span className="font-extrabold text-2xl tracking-tight text-white uppercase italic">BPA Solutions</span>
              </div>
              <p className="text-slate-400 text-lg leading-relaxed max-w-sm"> Empowering your digital growth with precision auditing and verified data collection. </p>
            </div>
            <div className="space-y-6">
              <h4 className="text-white font-bold text-sm uppercase tracking-widest border-b border-slate-800 pb-3 inline-block">Contact Details</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3"> <SafeIcon icon={FiUser} className="text-blue-500 mt-1" /> <div> <p className="text-white font-medium">Anish Malhotra</p> <p className="text-xs text-slate-500">BPA Solutions</p> </div> </div>
                <div className="flex items-start gap-3"> <SafeIcon icon={FiMapPin} className="text-blue-500 mt-1" /> <p className="text-sm">81-83 Campbell St. Surry Hills,<br />Sydney NSW 2010</p> </div>
                <a href="mailto:support@bpasolutions.au" className="flex items-center gap-3 hover:text-blue-400 transition-colors group"> <SafeIcon icon={FiMail} className="text-blue-500 group-hover:scale-110 transition-transform" /> <span className="text-sm font-medium">support@bpasolutions.au</span> </a>
                <a href="tel:+61425228778" className="flex items-center gap-3 hover:text-blue-400 transition-colors group"> <SafeIcon icon={FiPhone} className="text-blue-500 group-hover:scale-110 transition-transform" /> <span className="text-sm font-medium">+61 425 228 778</span> </a>
                <a href="https://www.bpasolutions.au" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-blue-400 transition-colors group"> <SafeIcon icon={FiExternalLink} className="text-blue-500 group-hover:scale-110 transition-transform" /> <span className="text-sm font-medium">www.bpasolutions.au</span> </a>
              </div>
            </div>
            <div className="flex flex-col justify-center lg:items-end">
              <div className="bg-slate-800/50 p-8 rounded-3xl border border-slate-700/50 w-full max-w-sm lg:text-right shadow-2xl">
                <h4 className="text-white font-black mb-4 uppercase tracking-widest text-sm">Ready to automate?</h4>
                <p className="text-sm text-slate-400 mb-8 font-medium"> Schedule a discovery session to see how smart forms and waivers can transform your business intake. </p>
                <a href="https://tidycal.com/anishmmalhotra/initial-20-minutes-discussion" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all shadow-lg shadow-blue-600/20 active:scale-95 w-full sm:w-auto" > <SafeIcon icon={FiCalendar} /> Book a Call </a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <p>© {new Date().getFullYear()} BPA Solutions. All rights reserved.</p>
            <div className="flex gap-6"> <span className="hover:text-slate-300 cursor-pointer transition-colors">Privacy Policy</span> <span className="hover:text-slate-300 cursor-pointer transition-colors">Terms of Service</span> </div>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {showIntroModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeIntro} className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" />
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="bg-white rounded-[2rem] shadow-2xl w-full max-w-xl relative z-10 overflow-hidden border border-slate-200" >
              <button onClick={closeIntro} className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 transition-all bg-slate-50 rounded-full" aria-label="Close" > <SafeIcon icon={FiX} className="text-xl" /> </button>
              <div className="p-8 sm:p-12">
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-8"> <SafeIcon icon={FiZap} className="text-blue-600 text-3xl" /> </div>
                <h2 className="text-3xl font-black text-slate-900 mb-6 leading-tight tracking-tight uppercase"> Verified Business Intake & Consent </h2>
                <div className="space-y-5 text-slate-600 mb-10 font-medium">
                  <p> This is a live demonstration of <strong>BPA Solutions Smart Forms</strong>. </p>
                  <p> Experience how we automate enquiries, intake, and <strong>digital waivers</strong> with real-time identity verification. </p>
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-3">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Platform Capabilities:</p>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-3 text-sm font-bold text-slate-700"> <SafeIcon icon={FiCheckCircle} className="text-green-500 shrink-0" /> <span>Verified Enquiries & Bookings</span> </li>
                      <li className="flex items-center gap-3 text-sm font-bold text-slate-700"> <SafeIcon icon={FiCheckCircle} className="text-green-500 shrink-0" /> <span>Signed Digital Waivers & Consent</span> </li>
                      <li className="flex items-center gap-3 text-sm font-bold text-slate-700"> <SafeIcon icon={FiCheckCircle} className="text-green-500 shrink-0" /> <span>Automated Workflow & Logging</span> </li>
                    </ul>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button onClick={scrollToDemo} className="flex-grow bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-2 active:scale-95" > Try a Demo Form <SafeIcon icon={FiArrowRight} /> </button>
                  <button onClick={closeIntro} className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-colors" > Explore </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;